import type { BurndownReport, ForecastResponse } from '@testflow/shared';
import { useTranslation } from 'react-i18next';
import { QueryErrorNotice } from '../../query-error-notice.js';
import { BurndownChart } from '../burndown-chart.js';

export interface ForecastControls {
  onCalculate: () => void;
  narrative: ForecastResponse['narrative'] | undefined;
  isSuccess: boolean;
  planName: string;
}

export function ForecastCard({
  burndown,
  isBurndownError,
  onRetrySummary,
  forecast,
}: {
  burndown: BurndownReport | undefined;
  isBurndownError: boolean;
  onRetrySummary: () => void;
  forecast?: ForecastControls | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';

  return (
    <section className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-bold text-text-primary">{t('reporting.rep4')}</h2>
        <span className="font-mono text-[10.5px] text-text-muted">REP-4</span>
      </div>
      <p className="mb-3 text-[10px] text-text-muted">{t('reporting.scopePlan')}</p>
      {/* Review Task 11: `isSummaryError` deckte nur die REP-1/2/3/5-Kacheln ab (die hängen
          an `summary`) — schlug NUR `report-burndown` fehl, blieb die Summary des Blocks gesetzt, die
          REP-4-Karte wurde gerendert, aber `burndown` war `undefined` → `{burndown ? … :
          null}` ließ eine betitelte, leere Karte stehen. Gleicher Grundsatz wie #89: ein
          Query-Fehler darf nie wie ein Leerzustand aussehen. `onRetrySummary` refetcht
          bereits summary UND burndown dieses Plans. */}
      {isBurndownError ? (
        <QueryErrorNotice onRetry={onRetrySummary} />
      ) : burndown ? (
        <>
          <BurndownChart data={burndown} lang={lang} />
          {/* #98.1: benannte Legende als HTML unter dem SVG (statt im Chart selbst) — die
              drei Swatches spiegeln 1:1 die Farb-/Strich-Optik der SVG-Linien
              (burndown-chart.tsx): Ideallinie gestrichelt in --status-open, Ist-Verlauf
              durchgezogen in --brand-primary, Heute-Markierung in --brand-accent. */}
          <div className="mt-2 flex flex-wrap items-center gap-4 text-[10.5px] text-text-muted">
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="h-0 w-4 border-t-2 border-dashed"
                style={{ borderColor: 'var(--status-open)' }}
              />
              {t('reporting.chart.ideal')}
            </span>
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="h-0.5 w-4"
                style={{ backgroundColor: 'var(--brand-primary)' }}
              />
              {t('reporting.chart.actual')}
            </span>
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="h-3 w-0.5"
                style={{ backgroundColor: 'var(--brand-accent)' }}
              />
              {t('reporting.chart.today')}
            </span>
          </div>
        </>
      ) : null}
      <div className="mt-3 flex items-center gap-3">
        {forecast ? (
          <button
            type="button"
            onClick={forecast.onCalculate}
            className="rounded-btn border border-brand-accent px-3 py-1 text-xs text-brand-accent"
          >
            ✦ {t('reporting.etaLoad')} · {forecast.planName}
          </button>
        ) : null}
        {burndown?.eta.projectedDate ? (
          <span className="text-xs text-text-body">
            {t('reporting.projected', { date: burndown.eta.projectedDate })} ·{' '}
            {t('reporting.runsPerDay', {
              n: burndown.eta.runsPerDay.toFixed(1),
            })}
          </span>
        ) : null}
      </div>
      {forecast?.narrative ? (
        <div className="mt-2 rounded-btn bg-[var(--tint-blue)] px-3 py-2 text-xs text-text-body">
          ✦ {forecast.narrative.text_de}
        </div>
      ) : forecast?.isSuccess ? (
        <div className="mt-2 text-xs text-text-muted">{t('reporting.etaDegraded')}</div>
      ) : null}
    </section>
  );
}
